import { create } from "zustand";
import { Platform, SocialAccount } from "@/types";
import { getFirebaseServices } from "@/lib/firebase/client";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const MAX_ACCOUNTS = 15;

interface AccountStore {
  accounts: SocialAccount[];
  activeAccountId: string | null;
  activeAccount: SocialAccount | null;
  userId: string | null;
  initialized: boolean;
  initListener: () => () => void;
  addAccount: (data: { platform: Platform; handle: string }) => Promise<void>;
  removeAccount: (id: string) => Promise<void>;
  updateAccount: (id: string, payload: Partial<SocialAccount>) => Promise<void>;
  setActiveAccount: (id: string) => void;
  incrementSlot: (id: string) => void;
  decrementSlot: (id: string) => void;
}

export const useAccountStore = create<AccountStore>((set, get) => ({
  accounts: [],
  activeAccountId: null,
  activeAccount: null,
  userId: null,
  initialized: false,
  initListener: () => {
    const services = getFirebaseServices();
    if (!services) {
      console.warn("Firebase not configured, account store disabled");
      return () => {};
    }

    const { auth, db } = services;
    let unsubSnapshot: (() => void) | null = null;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      // Always clean up any previous snapshot listener when auth state changes
      if (unsubSnapshot) {
        try {
          unsubSnapshot();
        } catch (e) {
          console.warn('Error unsubscribing previous accounts snapshot', e);
        }
        unsubSnapshot = null;
      }

      if (!user) {
        set({ accounts: [], activeAccountId: null, activeAccount: null, userId: null, initialized: true });
        return;
      }

      set({ userId: user.uid });

      const q = query(
        collection(db, "socialAccounts"),
        where("userId", "==", user.uid)
      );

      unsubSnapshot = onSnapshot(q, (snapshot) => {
        const accounts: SocialAccount[] = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        })) as SocialAccount[];

        set((state) => {
          // Try to restore from localStorage first, then fall back to current or first account
          let activeId = state.activeAccountId;
          if (!activeId && typeof window !== 'undefined') {
            const stored = localStorage.getItem('activeAccountId');
            if (stored && accounts.some(acc => acc.id === stored)) {
              activeId = stored;
            }
          }
          activeId = activeId ?? accounts[0]?.id ?? null;
          const activeAcc = accounts.find(acc => acc.id === activeId) ?? null;
          return {
            accounts,
            activeAccountId: activeId,
            activeAccount: activeAcc,
            initialized: true,
          };
        });
      });
    });

    return () => {
      try {
        unsubAuth();
      } catch (e) {
        console.warn('Error unsubscribing auth listener', e);
      }
      if (unsubSnapshot) {
        try {
          unsubSnapshot();
        } catch (e) {
          console.warn('Error unsubscribing accounts snapshot on cleanup', e);
        }
        unsubSnapshot = null;
      }
    };
  },
  addAccount: async ({ platform, handle }) => {
    const state = get();
    if (state.accounts.length >= MAX_ACCOUNTS) {
      throw new Error(`Limit reached (${MAX_ACCOUNTS} accounts).`);
    }
    if (!state.userId) {
      throw new Error("User not logged in.");
    }

    const services = getFirebaseServices();
    if (!services) {
      throw new Error("Firebase not configured.");
    }

    // Prevent duplicates: check existing accounts for same user + platform + handle (case-insensitive)
    try {
      const normalizedHandle = handle.trim().toLowerCase();
      const existingQuery = query(
        collection(services.db, "socialAccounts"),
        where("userId", "==", state.userId),
        where("platform", "==", platform)
      );
      const snapshot = await getDocs(existingQuery);
      for (const ds of snapshot.docs) {
        const existingHandle = (ds.data()?.handle || "").toString().trim().toLowerCase();
        if (existingHandle === normalizedHandle) {
          throw new Error("This social account has already been added.");
        }
      }
    } catch (err) {
      // rethrow known errors, otherwise continue (getDocs should not fail normally)
      if (err instanceof Error && err.message === "This social account has already been added.") throw err;
    }

    await addDoc(collection(services.db, "socialAccounts"), {
      userId: state.userId,
      platform,
      handle: handle.trim(),
      externalId: null,
      metadata: {},
      slotsUsed: 0,
      slotsLimit: 3,
      status: "pending",
      createdAt: serverTimestamp(),
    });
  },
  removeAccount: async (id) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase not configured.");
    await deleteDoc(doc(services.db, "socialAccounts", id));
  },
  updateAccount: async (id, payload) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase not configured.");
    await updateDoc(doc(services.db, "socialAccounts", id), payload);
  },
  setActiveAccount: (id) => {
    // Persist to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('activeAccountId', id);
    }
    set((state) => ({
      activeAccountId: id,
      activeAccount: state.accounts.find(acc => acc.id === id) ?? null,
    }));
  },
  incrementSlot: (id) =>
    set((state) => ({
      accounts: state.accounts.map((acc) => {
        if (acc.id !== id) return acc;
        if (acc.slotsUsed >= acc.slotsLimit) {
          throw new Error("All slots filled for this account.");
        }
        return { ...acc, slotsUsed: acc.slotsUsed + 1 };
      }),
    })),
  decrementSlot: (id) =>
    set((state) => ({
      accounts: state.accounts.map((acc) =>
        acc.id === id && acc.slotsUsed > 0
          ? { ...acc, slotsUsed: acc.slotsUsed - 1 }
          : acc
      ),
    })),
}));
